import { useReducer, useState } from 'react';
import { AssetsRequests } from 'src/requests/assets';
import { CustomFile, UploadControl, UploadControlItem } from './types';

const createUploadItem = (
  file: CustomFile,
  salt: number = 1,
): UploadControlItem => ({
  id: Date.now() + salt,
  file: Object.assign(file, {
    preview: URL.createObjectURL(file),
  }),
});

type Actions =
  | ['ADD', UploadControlItem[]]
  | ['REMOVE', UploadControlItem[]]
  | ['CLEAR']
  | ['UPDATE', UploadControlItem];

const reducer = (state: UploadControlItem[], [action, payload]: Actions) => {
  switch (action) {
    case 'ADD':
      return [...state, ...payload];
    case 'REMOVE':
      return state.filter(({ id }) => !payload.some((item) => item.id === id));
    case 'UPDATE':
      return state.map((item) => (item.id === payload.id ? payload : item));
    case 'CLEAR':
      return [];
    default:
      return state;
  }
};

export const useUploadController = (): UploadControl => {
  const [isUploading, setIsUploading] = useState(false);
  const [files, dispatch] = useReducer(reducer, []);

  const addFiles = (newFiles: CustomFile[]) => {
    const items = newFiles.map(createUploadItem);
    dispatch(['ADD', items]);
    return items;
  };

  const removeFiles = (items: UploadControlItem[]) => {
    dispatch(['REMOVE', items]);
  };

  const removeAllFiles = () => {
    dispatch(['CLEAR']);
  };

  const uploadSingleFile = async (file: CustomFile) => {
    const [uploadFile] = addFiles([file]);
    const results = await uploadToPublic(uploadFile);
    removeFiles([uploadFile]);
    return results;
  };

  const uploadToPublic = async (singleFile?: UploadControlItem) => {
    const archives = singleFile ? [singleFile] : files;
    setIsUploading(true);
    const promises = archives.map(async (item) =>
      AssetsRequests.uploadPublicFile(item.file, (percentage, speed) => {
        dispatch(['UPDATE', { ...item, progress: percentage, speed }]);
      }).then((response) => {
        dispatch(['UPDATE', { ...item, isCompleted: true }]);
        return response;
      }),
    );
    const results = await Promise.all(promises);
    setIsUploading(false);
    return results;
  };

  const updateFile = (data: UploadControlItem) => {
    dispatch(['UPDATE', data]);
  };

  return {
    files,
    addFiles,
    removeFiles,
    uploadToPublic,
    removeAllFiles,
    uploadSingleFile,
    isUploading,
    updateFile,
  };
};
